import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Area, Bar, CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { api } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { formatShortDate, monthRange, shiftMonth, sumStats, todayIso } from '../../utils/dateBuckets';
import { Card } from '../ui/Card';
import { Spinner } from '../ui/Spinner';
import { ChartTooltip, chartColors } from './ChartTooltip';
import { TrendStat } from './TrendStat';

export function MonthView() {
  const { currentSiteId } = useAuth();
  const [yearMonth, setYearMonth] = useState(() => todayIso().slice(0, 7));
  const prevYearMonth = shiftMonth(yearMonth, -1);
  const range = monthRange(yearMonth);
  const prevRange = monthRange(prevYearMonth);

  const current = useQuery({
    queryKey: ['daily-stats', currentSiteId, range.from, range.to],
    queryFn: () => api.dailyStats(currentSiteId!, range.from, range.to),
    enabled: !!currentSiteId,
  });
  const previous = useQuery({
    queryKey: ['daily-stats', currentSiteId, prevRange.from, prevRange.to],
    queryFn: () => api.dailyStats(currentSiteId!, prevRange.from, prevRange.to),
    enabled: !!currentSiteId,
  });

  const days = current.data?.days ?? [];
  const chartData = days.map((d) => ({ ...d, label: formatShortDate(d.date) }));
  const totals = sumStats(days);
  const prevTotals = sumStats(previous.data?.days ?? []);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <label htmlFor="month-picker" className="text-sm text-slate-500 dark:text-slate-400">
          Monat:
        </label>
        <input
          id="month-picker"
          type="month"
          value={yearMonth}
          onChange={(e) => e.target.value && setYearMonth(e.target.value)}
          className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
        />
      </div>

      {current.isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <TrendStat label="Besucher" value={totals.visitors} previousValue={prevTotals.visitors} />
            <TrendStat label="Sessions" value={totals.sessions} previousValue={prevTotals.sessions} />
            <TrendStat label="Conversions" value={totals.conversions} previousValue={prevTotals.conversions} />
            <TrendStat label="Seitenaufrufe" value={totals.pageViews} previousValue={prevTotals.pageViews} />
          </div>

          <Card title={`${yearMonth} im Detail`}>
            <ResponsiveContainer width="100%" height={320}>
              <ComposedChart data={chartData}>
                <defs>
                  <linearGradient id="monthVisitors" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={chartColors.visitors} stopOpacity={0.45} />
                    <stop offset="95%" stopColor={chartColors.visitors} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
                <XAxis dataKey="label" stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} interval={Math.floor(days.length / 10)} />
                <YAxis stroke="#64748b" fontSize={12} allowDecimals={false} tickLine={false} axisLine={false} />
                <Tooltip content={<ChartTooltip />} />
                <Area
                  type="monotone"
                  dataKey="visitors"
                  name="Besucher"
                  stroke={chartColors.visitors}
                  strokeWidth={2}
                  fill="url(#monthVisitors)"
                />
                <Line type="monotone" dataKey="sessions" name="Sessions" stroke={chartColors.sessions} strokeWidth={2} dot={false} />
                <Bar dataKey="conversions" name="Conversions" fill={chartColors.conversions} radius={[3, 3, 0, 0]} barSize={10} />
              </ComposedChart>
            </ResponsiveContainer>
          </Card>
        </>
      )}
    </div>
  );
}
